import React, {
  Fragment,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { isEqual, isNil } from 'lodash';
import cn from 'classnames';
import PerformanceBarPopover from 'legacy-src/components/assessment/Results/PerformanceBarPopover';
import TagWithDescriptionPopover from 'legacy-src/utility/TagWithDescriptionPopover';
import PracticeSkillsPopOver from 'chameleon-packages/units/components/PracticeSkills/PracticeSkillsPopOver';
import QuestionsTable from './QuestionsTable';
import { Icon } from 'legacy-packages/ft-cb';
import { QuestionTypes } from 'legacy/packages/unit-outline/src/constants';
import { LoadingSpinner } from 'legacy-packages/ft-cb';
import {
  normalizeQuestionsTableData,
  sortQuestionsByOtherTags,
  sortQuestionsByResults,
  sortQuestionsByResultsForStudent,
  sortQuestionsBySkills,
  getReferencesFromQuestions,
  getFirstQuestion,
  getSPPPartsTipsData,
  sortQuestionsByAverageTimeSpent,
  mapLearnosityItemReferenceToaverageTimeSpentInSeconds,
} from './utils';
import {
  getScoreTimeSpents,
  secondsToTime,
} from 'legacy-src/utility/progressHelpers';
import useQuestionPreview from 'chameleon-src/components/QuestionPreview/hooks/useQuestionPreview';
import ResponseDistribution from './ResponseDistribution';
import './styles.scss';
import { scoringGridAssignment } from 'queries';

const AVERAGE_TIME_SPENT = 'averageTimeSpent';
const USER_TIME_SPENT_IN_SECONDS = 'userTimeSpentInSeconds';

const ResultsQuestions = ({
  questions: items,
  showSingleResults = false,
  params,
  client,
  ...otherProps
}) => {
  const {
    studentId,
    resultsByRubric,
    studentScoringResults,
    teacherScoringResults,
    learnosityReportsByItemData,
    resultsByItem,
    isReady,
    students,
    meQuery: { me: { initId, isStudent } = {} },
    assignment: { categories, printToggle, scoreDisplay },
    otherTagColumnsFlag,
    isQbGamaAvailable,
    assessment: { isSpp },
    sessionTemplate,
    studentResponses,
    submissionData,
    results: report,
    pacingFlag,
    resultsDistractorsFlag,
    reviewModeFlag,
    isOtherPart,
  } = otherProps;

  const partialScoreDisplay =
    reviewModeFlag && scoreDisplay === 'partial' && isStudent;
  const [
    {
      hoverProps: questionPreviewProps,
      preview: questionPreview,
      previewContainer: questionPreviewContainer,
    },
  ] = useQuestionPreview({
    isAvailable: !isSpp && isQbGamaAvailable,
    items: React.useMemo(() => {
      const firstQuestion = getFirstQuestion(items);
      return firstQuestion ? [firstQuestion] : [];
    }, [items]),
    dynamicItems: React.useMemo(
      () => getReferencesFromQuestions(items),
      [items],
    ),
    hoveredItemParent: '.hover-question-parent',
    containerOffsetX: 80,
    containerOffsetY: 10,
    containerPlacement: 'bottom',
  });

  const currentStudentId = params.studentId || studentId || initId;

  const questionsWithNumbers = useMemo(
    () =>
      items.map((item, idx) => ({
        idx: idx + 1,
        ...item,
      })),
    [items],
  );

  const isAllFRQ = items.every(
    (item) => item.type === QuestionTypes.FREE_RESPONSE_QUESTION,
  );

  const [questionsData, setQuestionsData] = useState(questionsWithNumbers);
  const [sortBy, setSortBy] = useState([]);
  const [otherPartStudentResponses, setOtherPartStudentResponses] = useState(
    {},
  );
  const [otherPartSessionTemplate, setOtherPartSessionTemplate] = useState({
    items: [],
  });
  const [isLoadingOtherPartInfo, setIsLoadingOtherPartInfo] = useState(false);

  useEffect(() => {
    if (!isEqual(questionsWithNumbers, questionsData)) {
      setQuestionsData(questionsWithNumbers);
    }
  }, [questionsWithNumbers]);

  const getOtherPartData = async () => {
    setIsLoadingOtherPartInfo(true);
    const response = await client.query({
      query: scoringGridAssignment,
      variables: {
        assignmentId: params.ppcPartId,
        studentId: null,
      },
      fetchPolicy: 'cache-first',
    });
    const {
      data: {
        assignment: {
          studentResponses: otherPartStudentResponses,
          sessionTemplate: otherPartSessionTemplate,
        },
      },
    } = response;
    setIsLoadingOtherPartInfo(false);
    setOtherPartStudentResponses(JSON.parse(otherPartStudentResponses));
    setOtherPartSessionTemplate(JSON.parse(otherPartSessionTemplate));
  };

  useEffect(() => {
    try {
      if (isOtherPart) {
        getOtherPartData();
      }
    } catch (error) {
      setIsLoadingOtherPartInfo(false);
      // eslint-disable-next-line no-console
      console.error('Error fetching other part data:', error);
    }
  }, [isOtherPart, params.ppcPartId]);

  const normalizedData = React.useMemo(
    () =>
      normalizeQuestionsTableData(questionsData, {
        studentId,
        currentStudentId,
        showSingleResults,
        sortBy,
        ...otherProps,
        questionPreviewProps,
        studentResponses,
        sessionTemplate,
        otherPartStudentResponses,
        otherPartSessionTemplate,
        isOtherPart,
        isStudent,
      }),
    [
      items,
      questionsData,
      isReady,
      resultsByRubric,
      studentScoringResults,
      teacherScoringResults,
      resultsByItem,
      currentStudentId,
      sortBy,
      questionPreviewProps,
      studentResponses,
      sessionTemplate,
      otherPartStudentResponses,
      otherPartSessionTemplate,
    ],
  );

  const showUnit = categories.match(/Unit/);
  const showTopic = categories.match(/Topic/);
  const showTaskModel = categories.match(/Task Model/);
  const showProficiencies = categories.match(/Proficiencies/);
  const headerTitle = isSpp ? 'Part' : 'Question Name';
  const disableSortBy = isSpp;
  const answerTipsData = isSpp
    ? getSPPPartsTipsData(
        report,
        students,
        studentResponses,
        submissionData,
        sessionTemplate,
      )
    : [];

  let headerData = [
    {
      Header: headerTitle,
      accessor: 'question',
      rowClassName: 'border-b border-gray-300',
      disableSortBy,
      Cell: (cellData) => {
        const { value, row } = cellData;
        const {
          original: {
            children,
            learnosityItemReference,
            otherArgs: { questionPreviewProps },
          },
        } = row;
        const [questionId, questionText] = value;
        const [QnNumber, QnTittle] = questionText.split('*');
        const qTitle = isSpp ? <strong>{QnTittle}</strong> : QnTittle;

        return (
          <div className="flex flex-row">
            {!isSpp && <span className="font-bold">Q{QnNumber}</span>}
            <span
              role="link"
              tabIndex="0"
              className={cn('font-normal self-center', {
                'hover:underline': isNil(children) && !isSpp,
                'ml-8': !isSpp,
              })}
              {...questionPreviewProps}
              data-itemid={questionId}
              data-reference={learnosityItemReference}
            >
              {partialScoreDisplay ? '' : qTitle}
            </span>
          </div>
        );
      },
    },
  ];
  if (otherTagColumnsFlag) {
    // Unit column
    if (showUnit) {
      headerData.push({
        Header: 'Unit',
        accessor: 'units',
        Cell: ({ value }) => {
          if (!isNil(value)) {
            return <TagWithDescriptionPopover tags={value} />;
          }
          return null;
        },
      });
    }
    // Topic column
    if (showTopic) {
      headerData.push({
        Header: 'Topic',
        accessor: 'topics',
        disableSortBy,
        Cell: ({ value }) => {
          if (!isNil(value)) {
            return <TagWithDescriptionPopover tags={value} />;
          }
          return null;
        },
      });
    }
    // Task Model column
    if (showTaskModel) {
      headerData.push({
        Header: 'Task Model',
        accessor: 'taskModels',
        Cell: ({ value }) => {
          if (!isNil(value)) {
            return <TagWithDescriptionPopover tags={value} />;
          }
          return null;
        },
      });
    }
    if (showProficiencies) {
      headerData.push({
        Header: 'Proficiencies',
        accessor: 'proficiencies',
        Cell: ({ value }) => {
          if (!isNil(value)) {
            return <TagWithDescriptionPopover tags={value} />;
          }
          return null;
        },
      });
    }
  }
  headerData.push({
    Header: 'Skills',
    accessor: 'skills',
    disableSortBy,
    Cell: ({ value }) => {
      if (!isNil(value)) {
        return (
          <div className="flex">
            <PracticeSkillsPopOver skillsList={value} />
          </div>
        );
      }
      return null;
    },
  });
  if (pacingFlag && !printToggle && !isSpp) {
    headerData.push({
      Header:
        isStudent || showSingleResults ? 'Time Spent' : 'Average Time Spent',
      accessor:
        isStudent || showSingleResults
          ? USER_TIME_SPENT_IN_SECONDS
          : AVERAGE_TIME_SPENT,
      disableSortBy,
      floatRight: true,
      Cell: ({ value }) => {
        if (!isNil(value)) {
          return <div className="avg-spent-time">{value}</div>;
        }
        return null;
      },
    });
  }

  if (resultsDistractorsFlag && !isStudent && !isAllFRQ) {
    headerData.push({
      Header: 'Response Distribution',
      accessor: 'responseDistribution',
      centered: true,
      disableSortBy: true,
      Cell: ({ value }) => {
        if (!value) return null;
        const { responseDistribution, rightAnswer, questionType } = value;
        return (
          <div className="flex justify-center">
            <ResponseDistribution
              questionType={questionType}
              responseDistribution={responseDistribution}
              rightAnswer={rightAnswer}
            />
          </div>
        );
      },
    });
  }

  if (
    resultsDistractorsFlag &&
    showSingleResults &&
    !isStudent &&
    studentId &&
    !isAllFRQ
  ) {
    headerData.push({
      Header: 'Response',
      accessor: 'response',
      disableSortBy: true,
      centered: true,
      Cell: ({ value }) => {
        if (!value) return null;
        const { studentResponse, isCorrect } = value;
        const iconName = isCorrect ? 'correct' : 'incorrect';
        if (studentResponse) {
          return (
            <div className="flex justify-center">
              <div>
                <Icon
                  className={`${iconName}-answer-icon`}
                  name={iconName}
                  title={`${iconName} answer`}
                />
                {studentResponse}
              </div>
            </div>
          );
        }
        return null;
      },
    });
  }

  headerData.push({
    Header: showSingleResults ? 'Results (pts.)' : 'Class Results',
    accessor: 'performance',
    disableSortBy,
    floatRight: true,
    centered: false,
    Cell: (cellData) => {
      const { value, row } = cellData;
      const {
        original: {
          learnosityReportMaxScore,
          learnosityReportScoreParsed,
          textColor,
          children,
        },
      } = row;
      return (
        <div
          className={cn('flex justify-end', {
            'cursor-pointer': !isSpp,
          })}
        >
          {showSingleResults ? (
            isNil(learnosityReportScoreParsed) &&
            isNil(learnosityReportMaxScore) ? (
              <span className="italic">No Results</span>
            ) : (
              <div
                className={cn('font-normal text-default', {
                  [textColor]: isNil(children),
                })}
              >
                <span>
                  {learnosityReportScoreParsed}/{learnosityReportMaxScore}
                </span>
                {!isNil(children) && <span className="ml-1">Total</span>}
              </div>
            )
          ) : (
            !isSpp && (
              <PerformanceBarPopover
                isWeighed
                showScore={false}
                stacks={value}
                contentOffset="0 -2rem 0 0"
                studentUrl={`/${params.subject}/assessments/results/${params.assignmentId}/students`}
              />
            )
          )}
        </div>
      );
    },
  });

  const headers = React.useMemo(() => headerData, [items]);

  const handleSorting = useCallback(
    ({ sortBy = [] }) => {
      if (sortBy.length) {
        const [sortInfo] = sortBy;
        const { id: sortId, desc: isDescending } = sortInfo;
        if (sortId === 'performance') {
          const sortedAscendingData = showSingleResults
            ? sortQuestionsByResultsForStudent(
                [...questionsWithNumbers],
                learnosityReportsByItemData,
                students,
                parseInt(currentStudentId, 10),
              )
            : sortQuestionsByResults(
                [...questionsWithNumbers],
                learnosityReportsByItemData,
                students,
              );
          const sortedDataCopy = [...sortedAscendingData];
          if (isDescending) {
            setQuestionsData(sortedAscendingData);
          } else {
            setQuestionsData(sortedDataCopy.reverse());
          }
        } else if (sortId === 'question') {
          const sortedData = questionsWithNumbers.sort((a, b) => a.idx - b.idx);
          const sortedDataCopy = [...sortedData];
          if (isDescending) {
            setQuestionsData(sortedDataCopy.reverse());
          } else {
            setQuestionsData(sortedData);
          }
        } else if (
          sortId === USER_TIME_SPENT_IN_SECONDS ||
          sortId === AVERAGE_TIME_SPENT
        ) {
          const learnosityItemReferenceToaverageTimeSpentInSecondsMap =
            mapLearnosityItemReferenceToaverageTimeSpentInSeconds(
              normalizedData,
            );
          const sortedData = sortQuestionsByAverageTimeSpent(
            questionsWithNumbers,
            learnosityItemReferenceToaverageTimeSpentInSecondsMap,
            isDescending,
          );
          setQuestionsData(sortedData);
        } else if (
          ['skills', 'units', 'topics', 'taskModels', 'proficiencies'].includes(
            sortId,
          )
        ) {
          let sortedData;
          if (sortId === 'skills') {
            sortedData = sortQuestionsBySkills(
              [...questionsWithNumbers],
              isDescending,
            );
          } else {
            sortedData = sortQuestionsByOtherTags(
              [...questionsWithNumbers],
              isDescending,
              sortId,
            );
          }
          const sortedDataCopy = [...sortedData];
          if (isDescending) {
            setQuestionsData(sortedDataCopy.reverse());
          } else {
            setQuestionsData(sortedData);
          }
        }
      } else {
        setQuestionsData(questionsWithNumbers);
      }
      setSortBy(sortBy);
    },
    [learnosityReportsByItemData],
  );

  const totalTimeSpentPerUser = React.useMemo(() => {
    const { userTimeSpents } = getScoreTimeSpents(learnosityReportsByItemData);
    return secondsToTime(userTimeSpents[currentStudentId]);
  }, [learnosityReportsByItemData]);

  return (
    <Fragment>
      {isLoadingOtherPartInfo ? (
        <LoadingSpinner />
      ) : (
        <React.Fragment>
          {pacingFlag && !printToggle && !isSpp && isStudent && (
            <div className="flex w-full justify-between bg-[#F1F1F2] px-10">
              <div>
                <h4>Total Time Spent</h4>
              </div>
              <div>
                <h4>{totalTimeSpentPerUser}</h4>
              </div>
            </div>
          )}
          <QuestionsTable
            data={normalizedData}
            headers={headers}
            router={otherProps.router}
            isManualSort
            handleSort={handleSorting}
            answerTipsData={answerTipsData}
            reviewModeFlag={reviewModeFlag}
            {...otherProps}
          />
        </React.Fragment>
      )}
      {!partialScoreDisplay &&
        React.cloneElement(questionPreviewContainer, {
          children: questionPreview,
        })}
    </Fragment>
  );
};

function mapStateToProps({ featureFlag }) {
  return {
    reviewModeFlag: featureFlag.review_mode,
    otherTagColumnsFlag: featureFlag.results_other_tag_columns,
    pacingFlag: featureFlag.pacing,
    resultsDistractorsFlag: featureFlag.results_distractors,
    isQbGamaAvailable: featureFlag.poc_qb_preview_approach_gama,
  };
}

export default compose(connect(mapStateToProps))(ResultsQuestions);
